/* eslint-disable consistent-return, no-return-assign */
import {
  Actions,
  AddEthereumChainParameter,
  ProviderConnectInfo,
  ProviderRpcError,
  WatchAssetParameters,
  Connector,
} from '@web3-react/types'
import { SUPRA_MAINNET_CHAIN_ID } from 'config/constants/chains'

export class NoStarKeyError extends Error {
  public constructor() {
    super('StarKey wallet not installed')
    this.name = NoStarKeyError.name
    Object.setPrototypeOf(this, NoStarKeyError.prototype)
  }
}

/**
 * Guide: StarKey injects window.starkey; Supra provider lives at window.starkey.supra.
 * Use this to detect and get the provider for web3-react.
 */
export function getStarKeyProvider(): StarKeyProvider | null {
  if (typeof window === 'undefined') return null
  const win = window as any
  if (!('starkey' in win)) return null
  const supra = win.starkey?.ethereum ?? null
  return supra
}

export function isStarKeyInjected(): boolean {
  return Boolean(getStarKeyProvider())
}

const STARKEY_PROVIDER_RETRY_DELAY_MS = 800
const STARKEY_PROVIDER_MAX_RETRIES = 3

async function getStarKeyProviderWithRetry(): Promise<StarKeyProvider | null> {
  for (let attempt = 0; attempt < STARKEY_PROVIDER_MAX_RETRIES; attempt++) {
    const provider = getStarKeyProvider()
    if (provider) return provider
    if (attempt < STARKEY_PROVIDER_MAX_RETRIES - 1) {
      await new Promise((resolve) => setTimeout(resolve, STARKEY_PROVIDER_RETRY_DELAY_MS))
    }
  }
  return null
}

function parseChainId(chainId: string | number) {
  if (typeof chainId === 'number') return chainId
  if (typeof chainId === 'string' && chainId.startsWith('0x')) {
    return Number.parseInt(chainId, 16)
  }
  return Number.parseInt(String(chainId), 10)
}

function normalizeAccounts(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((x): x is string => typeof x === 'string' && x.length > 0)
  }
  if (typeof value === 'string' && value.length > 0) return [value]
  return []
}

type StarKeyProvider = {
  request?: (args: { method: string; params?: any[] | Record<string, unknown> }) => Promise<any>
  connect?: () => Promise<string[] | unknown>
  account?: () => Promise<string | string[] | unknown>
  disconnect?: () => Promise<void>
  getChainId?: () => Promise<string | number>
  on?: (event: string, cb: (...args: any[]) => void) => void
  off?: (event: string, cb: (...args: any[]) => void) => void
  removeListener?: (event: string, cb: (...args: any[]) => void) => void
  isConnected?: () => boolean
}

export interface StarKeyConstructorArgs {
  actions: Actions
  onError?: (error: Error) => void
}

export class StarKey extends Connector {
  // Keep connector provider type aligned with web3-react custom connectors.
  public provider: any

  private eagerConnection?: Promise<void>

  constructor({ actions, onError }: StarKeyConstructorArgs) {
    super(actions, onError)
  }
  private async isomorphicInitialize(): Promise<void> {
    if (this.eagerConnection) return
    return (this.eagerConnection = (async () => {
      const provider = await getStarKeyProviderWithRetry()
      if (!provider) return
      this.provider = provider as StarKeyProvider
      this.provider.on('connect', ({ chainId }: ProviderConnectInfo): void => {
        this.actions.update({ chainId: parseChainId(chainId) })
      })

      this.provider.on('disconnect', (error: ProviderRpcError): void => {
        // 1013 indicates that MetaMask is attempting to reestablish the connection
        // https://github.com/MetaMask/providers/releases/tag/v8.0.0
        if (error?.code === 1013) {
          console.debug('StarKey logged connection error 1013: "Try again later"')
          return
        }
        this.actions.resetState()
        this.onError?.(error)
      })

      this.provider.on('chainChanged', (chainId: string): void => {
        this.actions.update({ chainId: parseChainId(chainId) })
      })

      this.provider.on('accountsChanged', (accounts: string[]): void => {
        if (accounts.length === 0) {
          this.actions.resetState()
        } else {
          this.actions.update({ accounts })
        }
      })
    })())
  }

  /** {@inheritdoc Connector.connectEagerly} */
  public async connectEagerly(): Promise<void> {
    const cancelActivation = this.actions.startActivation()

    try {
      await this.isomorphicInitialize()
      if (!this.provider) return cancelActivation()

      // Wallets may resolve eth_chainId and hang on eth_accounts pending user interaction, which may include changing
      // chains; they should be requested serially, with accounts first, so that the chainId can settle.
      const accounts = (await this.provider.request({
        method: 'eth_requestAccounts',
      })) as string[]
      if (!accounts.length) throw new Error('No accounts returned')
      const chainId = (await this.provider.request({
        method: 'eth_chainId',
      })) as string
      this.actions.update({ chainId: parseChainId(chainId), accounts })
    } catch (error) {
      // we should be able to use `cancelActivation` here, but on mobile, metamask emits a 'connect'
      // event, meaning that chainId is updated, and cancelActivation doesn't work because an intermediary
      // update has occurred, so we reset state instead
      this.actions.resetState()
    }
  }

  public async activate(desiredChainIdOrChainParameters?: number | AddEthereumChainParameter): Promise<void> {
    let cancelActivation: () => void
    if (!this.provider?.isConnected?.()) cancelActivation = this.actions.startActivation()

    // Always re-detect provider when user explicitly selects this wallet (avoid reusing previous wallet).
    this.eagerConnection = undefined
    this.provider = undefined

    return this.isomorphicInitialize()
      .then(async () => {
        if (!this.provider) throw new NoStarKeyError()
        // Wallets may resolve eth_chainId and hang on eth_accounts pending user interaction, which may include changing
        // chains; they should be requested serially, with accounts first, so that the chainId can settle.
        const accounts = (await this.provider.request({
          method: 'eth_requestAccounts',
        })) as string[]
        const chainId = (await this.provider.request({
          method: 'eth_chainId',
        })) as string
        const receivedChainId = parseChainId(chainId)
        const desiredChainId =
          typeof desiredChainIdOrChainParameters === 'number'
            ? desiredChainIdOrChainParameters
            : desiredChainIdOrChainParameters?.chainId

        // if there's no desired chain, or it's equal to the received, update
        if (!desiredChainId || receivedChainId === desiredChainId)
          return this.actions.update({ chainId: receivedChainId, accounts })
        const desiredChainIdHex = `0x${desiredChainId.toString(16)}`

        await this.provider.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: desiredChainIdHex,
              chainName: 'Supra Testnet',
              rpcUrls: ['https://rpc-multivm.supra.com/rpc/v1/eth/wallet_integration'],
              nativeCurrency: {
                name: 'Supra',
                symbol: 'Supra',
                decimals: 18,
              },
              blockExplorerUrls: ['https://multivm.suprascan.io'],
            },
          ],
        })
        // if we're here, we can try to switch networks
        return this.provider
          .request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: desiredChainIdHex }],
          })
          .catch((error: ProviderRpcError) => {
            // https://github.com/MetaMask/metamask-mobile/issues/3312#issuecomment-1065923294
            const errorCode = (error.data as any)?.originalError?.code || error.code

            // 4902 indicates that the chain has not been added to MetaMask and wallet_addEthereumChain needs to be called
            // https://docs.metamask.io/guide/rpc-api.html#wallet-switchethereumchain
            if (errorCode === 4902 && typeof desiredChainIdOrChainParameters !== 'number') {
              if (!this.provider) throw new Error('No provider')
              // if we're here, we can try to add a new network
              return this.provider.request({
                method: 'wallet_addEthereumChain',
                params: [
                  {
                    chainId: desiredChainIdHex,
                    chainName: 'Supra Testnet',
                    rpcUrls: ['https://rpc-multivm.supra.com/rpc/v1/eth/wallet_integration'],
                    nativeCurrency: {
                      name: 'Supra',
                      symbol: 'Supra',
                      decimals: 18,
                    },
                    blockExplorerUrls: ['https://multivm.suprascan.io'],
                  },
                ],
              })
            }

            throw error
          })
          .then(() => this.activate(desiredChainId))
      })
      .catch((error) => {
        cancelActivation?.()
        throw error
      })
  }

  public async watchAsset({ address, symbol, decimals, image }: WatchAssetParameters): Promise<true> {
    if (!this.provider?.request) throw new Error('No provider')

    return this.provider
      .request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address,
            symbol,
            decimals,
            image,
          },
        },
      })
      .then((success: any) => {
        if (!success) throw new Error('Rejected')
        return true
      })
  }
}
